import React, { useContext, useState } from "react";
import { Box, ButtonBase, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { dataEventoFormatada, statusEvento } from "../config/evento";
import { CORES } from "../utils/participante";

// Itens do menu agrupados por secao — `roles` controla quem ve cada item.
const SECOES = [
  {
    titulo: "Evento",
    itens: [
      { label: "Lista de Participantes", path: "/listagem", roles: ["admin", "editor", "usuario"] },
      { label: "Leitor QR Code", path: "/leitorqrcode", roles: ["admin", "usuario"] },
      { label: "Convite", path: "/convidados", roles: ["admin", "usuario"] },
      { label: "Sorteio", path: "/sorteio", roles: ["admin", "editor", "usuario"] },
      { label: "Inscrição Interna", path: "/inscricao-interna", roles: ["admin", "editor", "usuario"] },
    ],
  },
  {
    titulo: "Cadastros",
    itens: [
      { label: "Cadastrar Administrador", path: "/register", roles: ["admin"] },
      { label: "Cadastrar Usuário", path: "/cadastrarusuario", roles: ["admin"] },
      { label: "Listagem de Distritais", path: "/listagem-distritais", roles: ["admin"] },
      { label: "Cadastrar Distrital", path: "/register-distrital", roles: ["admin"] },
    ],
  },
  {
    titulo: "Importações",
    itens: [
      { label: "Importar Sympla", path: "/importar-sympla", roles: ["admin"] },
      { label: "Importar Mesas", path: "/importar-mesas", roles: ["admin"] },
    ],
  },
];

const ABAS = [
  { label: "Convidados", path: "/listagem", roles: ["admin", "editor", "usuario"] },
  { label: "Leitor QR", path: "/leitorqrcode", roles: ["admin", "usuario"] },
];

const STATUS = {
  andamento: { cor: "#3ddc84", texto: () => "Evento em andamento" },
  agendado: { cor: "#f2c14e", texto: () => `Evento em ${dataEventoFormatada()}` },
  encerrado: { cor: "#8a9bb0", texto: () => "Evento encerrado" },
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useContext(AuthContext);

  if (location.pathname === "/") {
    return null;
  }

  const role = user?.role ?? null;
  const isLoggedIn = !!user;
  const pode = (roles) => roles.includes(role);
  const status = STATUS[statusEvento()];

  const irPara = (path) => {
    navigate(path);
    setAnchorEl(null);
  };

  const sair = async () => {
    setAnchorEl(null);
    await logout();
    navigate("/equipe-acsp2026");
  };

  const secoesVisiveis = SECOES.map((secao) => ({
    ...secao,
    itens: secao.itens.filter((item) => pode(item.roles)),
  })).filter((secao) => secao.itens.length > 0);

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: CORES.navy,
        color: "#fff",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 4 },
          px: { xs: 2, md: 2.5 },
          minHeight: 52,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: isLoggedIn ? "pointer" : "default" }}
          onClick={() => isLoggedIn && navigate("/listagem")}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1.5px solid rgba(255,255,255,0.85)",
              display: "grid",
              placeItems: "center",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: 0.3,
              flexShrink: 0,
            }}
          >
            ACSP
          </Box>
          <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.18)", pl: 1.5, display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
              Dia do Comerciante
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)", lineHeight: 1.2 }}>
              Associação Comercial de São Paulo
            </Typography>
          </Box>
        </Box>

        {isLoggedIn && (
          <Box
            component="nav"
            sx={{
              display: "flex",
              gap: 0.5,
              p: 0.5,
              borderRadius: "8px",
              backgroundColor: "rgba(255,255,255,0.07)",
            }}
          >
            {ABAS.filter((aba) => pode(aba.roles)).map((aba) => {
              const ativa = location.pathname === aba.path;
              return (
                <ButtonBase
                  key={aba.path}
                  onClick={() => navigate(aba.path)}
                  sx={{
                    px: { xs: 1.25, sm: 1.75 },
                    py: 0.75,
                    borderRadius: "6px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    color: ativa ? CORES.navy : "rgba(255,255,255,0.85)",
                    backgroundColor: ativa ? "#fff" : "transparent",
                    transition: "background-color .15s",
                    "&:hover": { backgroundColor: ativa ? "#fff" : "rgba(255,255,255,0.08)" },
                  }}
                >
                  {aba.label}
                </ButtonBase>
              );
            })}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        {isLoggedIn && (
          <>
            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: status.cor }} />
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
                {status.texto()}
              </Typography>
            </Box>

            <IconButton
              aria-label="Abrir menu"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                color: "#fff",
                borderRadius: "8px",
                border: "1px solid rgba(255,255,255,0.25)",
                width: 32,
                height: 32,
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    width: 252,
                    borderRadius: "10px",
                    border: `1px solid ${CORES.borda}`,
                    boxShadow: "0 12px 32px rgba(11,42,69,0.16)",
                  },
                },
                list: { sx: { py: 0.5 } },
              }}
            >
              {secoesVisiveis.map((secao, i) => [
                i > 0 && <Divider key={`div-${secao.titulo}`} sx={{ my: 0.5 }} />,
                <Typography
                  key={`titulo-${secao.titulo}`}
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: "#8a97a6",
                  }}
                >
                  {secao.titulo}
                </Typography>,
                ...secao.itens.map((item) => {
                  const ativo = location.pathname === item.path;
                  return (
                    <MenuItem
                      key={item.path}
                      onClick={() => irPara(item.path)}
                      sx={{
                        mx: 0.75,
                        borderRadius: "6px",
                        fontSize: 13,
                        fontWeight: ativo ? 600 : 400,
                        color: CORES.texto,
                        backgroundColor: ativo ? "#eef3f9" : "transparent",
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: 32,
                      }}
                    >
                      {item.label}
                      {ativo && (
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: CORES.navy }} />
                      )}
                    </MenuItem>
                  );
                }),
              ])}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={sair}
                sx={{ mx: 0.75, borderRadius: "6px", fontSize: 13, color: CORES.vermelho, gap: 1, minHeight: 32 }}
              >
                <LogoutIcon sx={{ fontSize: 16 }} />
                Sair
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Header;
